// src/components/SystemRecommendation/components/HeroCard.tsx

import React from 'react';
import { RecommendationData, ViewMode } from '../types';
import SystemDiagram from './SystemDiagram';
import ConfidenceBadge from './ConfidenceBadge';

interface HeroCardProps {
  data: RecommendationData;
  viewMode: ViewMode;
}

const HeroCard: React.FC<HeroCardProps> = ({ data, viewMode }) => {
  const getOpeningLine = () => {
    if (viewMode === 'customer') {
      return "Based on what we know about your home today, this is the system we would choose.";
    }
    return "Recommended System";
  };

  const getClosingLine = () => {
    if (viewMode === 'customer') {
      return "If this were our home, this is the system we'd choose.";
    }
    return null;
  };

  return (
    <div className="hero-card">
      {viewMode === 'customer' && (
        <>
          <div className="match-percentage">
            <span className="match-number">{data.confidence.percentage}%</span>
            <span className="match-label">match for your home</span>
          </div>
          <h2 className="hero-title">Your Recommended Heating System</h2>
        </>
      )}
      {viewMode === 'technical' && (
        <h2 className="hero-title">✓ Recommended System</h2>
      )}

      <p className="opening-line">{getOpeningLine()}</p>

      <div className="hero-visual">
        <SystemDiagram
          systemType={data.systemType}
          viewMode={viewMode}
        />
      </div>

      <div className="hero-details">
        <h3 className="system-name">{data.systemName}</h3>

        {viewMode === 'customer' && (
          <p className="customer-benefit">
            Perfect for your {data.propertyContext.occupancy}-person home<br/>
            Endless hot water, lower bills
          </p>
        )}

        {viewMode === 'technical' && (
          <div className="technical-specs">
            <ConfidenceBadge confidence={data.confidence} />
            <div className="spec-grid">
              <div className="spec-item">
                <span className="spec-label">Heat Loss:</span>
                <span className="spec-value">
                  {data.heatLossKw} kW
                  {data.heatLossSource === 'assumed' && ' (calculated)'}
                  {data.heatLossSource === 'measured' && ' ✓'}
                </span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Flow Temp:</span>
                <span className="spec-value">{data.flowTemp}°C</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Occupancy:</span>
                <span className="spec-value">{data.occupancy} people</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Recommended Output:</span>
                <span className="spec-value">{data.recommendedOutput} kW</span>
              </div>
            </div>
            {data.specificModel && (
              <p className="model-recommendation">{data.specificModel}</p>
            )}
          </div>
        )}

        {viewMode === 'customer' && getClosingLine() && (
          <p className="closing-line">"{getClosingLine()}"</p>
        )}
      </div>
    </div>
  );
};

export default HeroCard;
